import React, { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { Range, Direction, getTrackBackground } from 'react-range'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume'
import { faVolumeUp } from '@fortawesome/pro-solid-svg-icons/faVolumeUp'
import { faVolumeDown } from '@fortawesome/pro-solid-svg-icons/faVolumeDown'
import { faVolumeSlash } from '@fortawesome/pro-solid-svg-icons/faVolumeSlash'
import { colors } from 'utils/colors'

const STEP = 1
const MIN = 0
const MAX = 100

interface Props {
  onChange(value: number): void
  disabled?: boolean
  isFloating?: boolean
}

const VolumeControl: React.FC<Props> = ({ onChange, disabled = false, isFloating }) => {
  const [volume, setVolume] = React.useState(MAX)
  const [isHovered, setIsHovered] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const rangeWrapperRef = useRef<HTMLDivElement>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!containerRef.current || !isHovered) return

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setPosition({
          top: rect.top - 105,
          left: rect.left + rect.width / 2 - 15,
        })
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isHovered])

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    },
    [],
  )

  const volumeChange = (values: number[]) => {
    setVolume(values[0])
    onChange(values[0])
  }

  const setMaxVolume = () => {
    setVolume(MAX)
    onChange(MAX)
  }

  const toggleMute = () => {
    const toggled = volume === MIN ? MAX : MIN
    setVolume(toggled)
    onChange(toggled)
  }

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = (setTimeout(() => {
      setIsHovered(false)
    }, 150) as unknown) as NodeJS.Timeout
  }

  const handleRangeMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setIsHovered(true)
  }

  const handleRangeMouseLeave = () => {
    hoverTimeoutRef.current = (setTimeout(() => {
      setIsHovered(false)
    }, 150) as unknown) as NodeJS.Timeout
  }

  const rangeContent = !disabled && isHovered && (
    <RangeWrapper
      ref={rangeWrapperRef}
      id="volume-control-range"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
      onMouseEnter={handleRangeMouseEnter}
      onMouseLeave={handleRangeMouseLeave}
    >
      <button onClick={setMaxVolume}>
        <FontAwesomeIcon icon={faVolumeUp} color="white" />
      </button>
      <Range
        direction={Direction.Up}
        values={[volume]}
        step={STEP}
        min={MIN}
        max={MAX}
        onChange={volumeChange}
        renderTrack={({ props, children }) => (
          <Track onMouseDown={props.onMouseDown} onTouchStart={props.onTouchStart}>
            <TrackBackground
              ref={props.ref}
              background={getTrackBackground({
                values: [volume],
                colors: [colors.white, colors.gray6],
                min: MIN,
                max: MAX,
                direction: Direction.Up,
              })}
            >
              {children}
            </TrackBackground>
          </Track>
        )}
        renderThumb={({ props }) => <Thumb {...props} />}
      />
      <button onClick={toggleMute}>
        <IconWrapper>
          {volume === 0 ? (
            <FontAwesomeIcon icon={faVolumeSlash} color="white" />
          ) : (
            <FontAwesomeIcon icon={faVolumeDown} color="white" />
          )}
        </IconWrapper>
      </button>
    </RangeWrapper>
  )

  return (
    <>
      <Container ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {createPortal(rangeContent, document.body)}
        <IconWrapper>
          {volume === 0 ? (
            <FontAwesomeIcon icon={faVolumeSlash} color={isFloating ? 'white' : 'grayShadeBlack2'} />
          ) : (
            <FontAwesomeIcon icon={faVolume} color={isFloating ? 'white' : 'grayShadeBlack2'} />
          )}
        </IconWrapper>
      </Container>
    </>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  flex-direction: column;
  position: relative;
`

const TrackHeight = '70px'

const RangeWrapper = styled.div`
  position: fixed;
  padding: 10px 0;
  background: linear-gradient(180deg, #6f6f6f 0%, #424242 100%);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 10000;
  pointer-events: auto;
  opacity: 1;
  transition: opacity 0.1s ease-in-out;
  transform: translateX(-50%);
`

const Track = styled.div`
  height: ${TrackHeight};
  width: 10;
  display: flex;
  flex-grow: 1;
  cursor: pointer;
`

const TrackBackground = styled.div<{ background: string }>(
  (p) => css`
    width: 5px;
    height: 100%;
    border-radius: 4px;
    background: ${p.background};
    align-self: center;
  `,
)

const Thumb = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 15px;
  height: 15px;
  background: white;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.colors.gray8};

  &:focus {
    outline: none;
  }
`

const IconWrapper = styled.div`
  box-sizing: border-box;
  margin: auto;
`

export default VolumeControl
